/**
 * Extension job processing utilities
 * Updated for direct messaging approach (v3.0)
 * 
 * This module provides utilities for working with job data received from the extension.
 * No API processing - jobs are handled directly in the frontend.
 */

import type { JobMarketDto } from 'src/contracts/job-market';

import type { ExtensionJobData } from './types';

// Debug mode flag
let debugMode = false;

/**
 * Initialize job processing utilities
 */
export function initializeJobProcessing(options: { debug?: boolean } = {}): void {
  debugMode = !!options.debug;
  
  if (debugMode) {
    console.log('[Extension Job Processing] Initialized');
  }
}

/**
 * Convert extension job data to JobMarketDto format
 */
export function convertExtensionJobsToJobMarketDto(extensionData: ExtensionJobData): JobMarketDto[] {
  if (!extensionData.jobs || !Array.isArray(extensionData.jobs)) {
    if (debugMode) {
      console.warn('[Extension Job Processing] Invalid jobs data received');
    }
    return [];
  }

  const convertedJobs = extensionData.jobs.map((job, index) => {
    // Generate a temporary ID if none exists
    const tempId = job.id || `ext_job_${Date.now()}_${index}`;
    
    // Debug logging
    if (debugMode && job.companyLogoUrl) {
      console.log('[Extension Job Processing] Job has companyLogoUrl:', job.companyLogoUrl);
    }
    
    const convertedJob = {
      // Direct mapping from extension to JobMarketDto
      id: tempId,
      title: job.title || null,
      company: job.company || null,
      location: job.location || null,
      description: job.description || null,
      salary: job.salary || null,
      postedDate: job.postedDate || null,
      jobUrl: job.jobUrl || null,
      companyLogoUrl: job.companyLogoUrl || null,
      platform: job.platform || null,
      jobType: job.jobType || null,
      workArrangement: job.workArrangement || null,
      requiredSkills: job.requiredSkills || null,
      applicantCount: job.applicantCount || null,
      isPRRequired: job.isPRRequired || false,
      userId: undefined,
      // Already applied detection from extension
      isAlreadyApplied: job.isAlreadyApplied || false,
      appliedDateUtc: job.appliedDateUtc || null,
    } as JobMarketDto;
    
    // Debug logging
    if (debugMode && convertedJob.companyLogoUrl) {
      console.log('[Extension Job Processing] Converted job has companyLogoUrl:', convertedJob.companyLogoUrl);
    }
    
    return convertedJob;
  });

  if (debugMode) {
    console.log('[Extension Job Processing] Converted jobs:', {
      originalCount: extensionData.jobs.length,
      convertedCount: convertedJobs.length,
      source: extensionData.source
    });
  }

  return convertedJobs;
}

/**
 * Validate extension job data
 */
export function validateExtensionJobData(data: any): data is ExtensionJobData {
  if (!data || typeof data !== 'object') {
    return false;
  }

  // Check required fields
  if (!data.jobs || !Array.isArray(data.jobs)) {
    return false;
  }

  if (!data.timestamp || !data.source) {
    return false;
  }

  // Validate each job has minimum required fields
  return data.jobs.every((job: any) => {
    if (!job || typeof job !== 'object') {
      return false;
    }
    
    return job.title && job.company;
  });
}

/**
 * Get job statistics from extension data
 */
export function getJobStatistics(extensionData: ExtensionJobData): {
  totalJobs: number;
  platforms: string[];
  companies: string[];
  locations: string[];
  averageJobsPerPlatform: number;
} {
  const jobs = extensionData.jobs || [];
  
  const platforms = [...new Set(jobs.map(job => job.platform).filter((p): p is string => Boolean(p)))];
  const companies = [...new Set(jobs.map(job => job.company).filter((c): c is string => Boolean(c)))];
  const locations = [...new Set(jobs.map(job => job.location).filter((l): l is string => Boolean(l)))];
  
  return {
    totalJobs: jobs.length,
    platforms,
    companies,
    locations,
    averageJobsPerPlatform: platforms.length > 0 ? Math.round(jobs.length / platforms.length) : 0
  };
}

/**
 * Filter jobs by criteria
 */
export function filterExtensionJobs(
  extensionData: ExtensionJobData, 
  filters: {
    platform?: string;
    location?: string;
    company?: string;
    minSalary?: number;
    maxSalary?: number;
  }
): ExtensionJobData {
  const filteredJobs = extensionData.jobs.filter(job => {
    // Platform filter
    if (filters.platform && job.platform?.toLowerCase() !== filters.platform.toLowerCase()) {
      return false;
    }
    
    // Location filter
    if (filters.location && !job.location?.toLowerCase().includes(filters.location.toLowerCase())) {
      return false;
    }
    
    // Company filter
    if (filters.company && !job.company?.toLowerCase().includes(filters.company.toLowerCase())) {
      return false;
    }
    
    // Salary filters (basic implementation)
    if ((filters.minSalary && job.salary && !containsSalaryAbove(job.salary, filters.minSalary)) ||
        (filters.maxSalary && job.salary && !containsSalaryBelow(job.salary, filters.maxSalary))) {
      return false;
    }
    
    return true;
  });

  return {
    ...extensionData,
    jobs: filteredJobs
  };
}

/**
 * Basic salary parsing helper
 */
function containsSalaryAbove(salaryText: string, minAmount: number): boolean {
  const numbers = salaryText.match(/\d+/g);
  if (!numbers) return false;
  return numbers.some(num => parseInt(num, 10) >= minAmount);
}

/**
 * Basic salary parsing helper
 */
function containsSalaryBelow(salaryText: string, maxAmount: number): boolean {
  const numbers = salaryText.match(/\d+/g);
  if (!numbers) return true; // If no salary specified, don't filter out
  return numbers.some(num => parseInt(num, 10) <= maxAmount);
}